import { Navigate, Route, Router, useLocation, useNavigate } from "@solidjs/router";
import { Show, createEffect, lazy, onCleanup, onMount } from "solid-js";
import { CommandPalette } from "./components/CommandPalette";
import { QuickAddModal } from "./components/QuickAddModal";
import { Sidebar } from "./components/Sidebar";
import { TokenPrompt } from "./components/TokenPrompt";
import { installShortcuts } from "./shortcuts";
import {
  drawerOpen,
  selectedId,
  setDrawerOpen,
  setPaletteOpen,
  tokenPromptOpen,
} from "./store/ui";
import styles from "./App.module.css";

const ListView = lazy(() => import("./views/ListView"));
const TreeView = lazy(() => import("./views/TreeView"));
const TodayView = lazy(() => import("./views/TodayView"));
const SidePanel = lazy(() => import("./components/SidePanel"));

function HamburgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M3 6h14M3 10h14M3 14h14"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" stroke-width="1.4" />
      <path d="M9.5 9.5L12 12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
    </svg>
  );
}

function Shell(props: { children?: any }) {
  const navigate = useNavigate();
  const location = useLocation();
  const viewTitle = () => {
    const p = location.pathname;
    if (p.startsWith("/today")) return "Today";
    if (p.startsWith("/tree")) return "Tree";
    if (p.startsWith("/project/")) return "Project";
    return "All issues";
  };

  // Lock body scroll while the mobile drawer is open so iOS momentum-scroll
  // doesn't bleed through behind the panel.
  createEffect(() => {
    if (typeof document !== "undefined") {
      document.body.style.overflow = drawerOpen() ? "hidden" : "";
    }
  });

  onMount(() => {
    const cleanup = installShortcuts(navigate);
    onCleanup(cleanup);
  });

  return (
    <div class={styles.shell}>
      <Sidebar />
      <Show when={drawerOpen()}>
        <div
          class={styles.backdrop}
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      </Show>
      <main class={styles.main}>
        <header class={styles.topbar}>
          <button
            type="button"
            class={styles.hamburger}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <HamburgerIcon />
          </button>
          <span class={styles.topbarTitle}>{viewTitle()}</span>
          <button
            type="button"
            class={styles.searchHint}
            onClick={() => setPaletteOpen(true)}
            aria-label="Open command palette"
          >
            <SearchIcon />
            <span>Search</span>
            <kbd class={styles.kbd}>⌘K</kbd>
          </button>
        </header>
        <section class={styles.content}>{props.children}</section>
      </main>
      <CommandPalette />
      <QuickAddModal />
      <Show when={selectedId() !== null}>
        <SidePanel />
      </Show>
    </div>
  );
}

export default function App() {
  return (
    <Show when={!tokenPromptOpen()} fallback={<TokenPrompt />}>
      <Router root={Shell}>
        <Route path="/" component={() => <Navigate href="/today" />} />
        <Route path="/today" component={TodayView} />
        <Route path="/list" component={ListView} />
        <Route path="/project/:name" component={ListView} />
        <Route path="/tree" component={TreeView} />
        <Route path="*" component={() => <Navigate href="/today" />} />
      </Router>
    </Show>
  );
}
